import * as Yup from 'yup';

import { currency } from '@FormModels/shared';

const amountMinError = 'Your loan must be at least £5k';
const amountMin = 5000;
const amountMax = 500000;
const amountMaxError = 'You can borrow up to £500,000';
const termMessage = 'Please select an option for term.';
const termRangeMessage = 'Please enter a valid term range (5-30)';

const purposes = [
    'Debt consolidation',
    'Home improvements',
    'Holiday',
    'Medical bills',
    'Property - Second home purchase',
    'Property - Buy-to-let purchase',
    'School fees / further education',
    'Tax bills',
    'Vehicle purchase',
    'Wedding',
    'Business purposes',
    'Contingency',
    'Other',
];

export const purposeArray = purposes
    .map((p) => ({ label: p, value: p }))
    .sort((a, b) => a.value.localeCompare(b.value));

export const formItems = {
    amount: {
        default: amountMin,
        label: 'How much do you want to borrow?',
        ...currency,
        maskProps: {
            min: amountMin,
            max: amountMax,
        },
        custom: {
            min: amountMin,
            max: amountMax,
        },
        validation: Yup.number()
            .typeError(amountMinError)
            .min(amountMin, amountMinError)
            .max(amountMax, amountMaxError)
            .required(amountMinError),
        transform: {
            target: 'requestedLoanAmount',
        },
    },
    term: {
        mask: 'number',
        validation: Yup.number()
            .required(termMessage)
            .typeError(termMessage)
            .min(5, termRangeMessage)
            .max(30, termRangeMessage),
        transform: {
            target: 'requestedLoanTerm',
            toRequest: (value: number) => +value,
        },
    },
    facilities: {
        transform: {
            target: 'facilities',
        },
        validation: Yup.array()
            .of(
                Yup.object({
                    allocationPurpose: Yup.string().nullable(),
                    allocationAmount: Yup.number()
                        .transform((v) =>
                            typeof v === 'number' && isNaN(v) ? undefined : v,
                        )
                        .nullable()
                        .notRequired(),
                }),
            )
            .test('amounts-required', function (facility) {
                const primaryAmount = facility?.[0]?.allocationAmount;
                const secondaryPurpose = facility?.[1]?.allocationPurpose;
                const secondaryAmount = facility?.[1]?.allocationAmount;

                if (!primaryAmount) {
                    return this.createError({
                        path: 'facilities[0].allocationAmount',
                        message: 'Please enter a value.',
                    });
                }

                if (secondaryPurpose && !secondaryAmount) {
                    return this.createError({
                        path: 'facilities[1].allocationAmount',
                        message: 'Please enter a value.',
                    });
                }
                return true;
            })
            .test(
                'allocations-equal-total',
                'Amounts must match the total loan amount.',
                function (facility) {
                    const amount = (this.parent as any)?.amount;
                    if (amount == null) return true;

                    const primaryAmount = facility?.[0]?.allocationAmount;
                    const hasSecondary = !!facility?.[1]?.allocationPurpose;

                    // Single purpose: primary must equal total
                    if (!hasSecondary) {
                        if (primaryAmount === amount) return true;
                        return this.createError({
                            path: 'facilities[0].allocationAmount',
                            message: 'Must equal the total loan amount',
                        });
                    }

                    // Two purposes: primary + secondary must equal total
                    const secondaryAmount = facility?.[1]?.allocationAmount;
                    if (
                        primaryAmount != null &&
                        secondaryAmount != null &&
                        primaryAmount + secondaryAmount === amount
                    )
                        return true;

                    return this.createError({
                        path: 'facilities[1].allocationAmount',
                        message:
                            'Primary and secondary amounts must equal the total loan amount.',
                    });
                },
            ),
    },
    debtConMainGoal: {
        transform: {
            target: 'debtConMainGoal',
        },
    },
};
